// Cross-frame channel used by the partner embed.
//
// The partner places several independent iframes on their page: the converter,
// the sign-in button (?embed=auth), and one button per plan (?embed=plan). The
// button frames are sized to their button, so they have no room to render the
// sign-in dialog or a plan-change confirmation — but the converter frame does.
// These helpers let a button frame hand the interaction to the converter frame.
//
// All frames are the same origin and sit under the same top-level page, so they
// share a storage partition and can talk over BroadcastChannel. A token written
// by one frame reaches the others through the `storage` listener in AppContext.
//
// If no converter frame answers (a button embedded on its own), the caller
// falls back to handling it locally — see the button components.

const CHANNEL = 'msgpdf-embed';
const ACK = 'ack';

export const REQUEST_AUTH = 'open-auth';
export const REQUEST_CHECKOUT = 'start-checkout';
export const REQUEST_ACCOUNT = 'open-account';

// Presence handshake, separate from the request/ACK protocol above.
//
// A button frame has to know whether a converter frame exists *before* the
// click, not during it: with no converter it opens a popup, and popup blockers
// only honour window.open in the same task as the user's gesture — there is no
// room to await a 250 ms ACK first. So both sides announce themselves at mount
// and the button frame remembers the answer.
const REQUEST_PRESENCE = 'converter-present?';
const PRESENT = 'converter-present';

// How long a button frame waits for the converter frame to answer before
// falling back. Same-origin BroadcastChannel delivery is effectively
// immediate; this is just a safety margin.
const ACK_TIMEOUT_MS = 250;

const supported = typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function';

// Converter frame: answer requests from button frames. `handlers` maps a
// request type to a function receiving the request payload. Returns an
// unsubscribe fn.
export function listenForEmbedRequests(handlers) {
  if (!supported) return () => {};
  const channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event) => {
    const { type, payload } = event?.data || {};
    // Announce ourselves to a button frame that booted first and is still
    // waiting to hear from us.
    if (type === REQUEST_PRESENCE) { channel.postMessage({ type: PRESENT }); return; }
    const handler = handlers[type];
    if (!handler) return;
    channel.postMessage({ type: ACK, forType: type });
    handler(payload);
  };
  // ...and to one that boots later. The partner ships the converter with
  // loading="lazy", so either frame can be the slow one; announcing on both
  // edges covers both orders.
  channel.postMessage({ type: PRESENT });
  return () => channel.close();
}

// Button frame: learn whether a converter frame shares this page. Calls
// `onPresent` once one answers — which may be immediately (it is already up) or
// later (it was lazy-loaded below the fold). Returns an unsubscribe fn.
export function watchConverterPresence(onPresent) {
  if (!supported) return () => {};
  const channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event) => {
    if (event?.data?.type === PRESENT) onPresent();
  };
  channel.postMessage({ type: REQUEST_PRESENCE });
  return () => channel.close();
}

// Button frame: ask a converter frame to take over. Resolves true if one
// answered, false if the caller should handle it locally.
export function requestFromConverterFrame(type, payload) {
  if (!supported) return Promise.resolve(false);
  return new Promise((resolve) => {
    const channel = new BroadcastChannel(CHANNEL);
    let settled = false;
    const finish = (answered) => {
      if (settled) return;
      settled = true;
      channel.close();
      resolve(answered);
    };
    channel.onmessage = (event) => {
      if (event?.data?.type === ACK && event.data.forType === type) finish(true);
    };
    channel.postMessage({ type, payload });
    setTimeout(() => finish(false), ACK_TIMEOUT_MS);
  });
}
